/**
 * Design tokens for the Benchmarking PDF report.
 *
 * All values are plain constants — no CSS variables, no design-system imports.
 * react-pdf does not have access to the browser's computed styles, so every
 * token that would normally come from @plurall/elo-v3 or Chakra must be
 * resolved here explicitly.
 */

// ---------------------------------------------------------------------------
// Brand palette
// ---------------------------------------------------------------------------
export const PDF_COLORS = {
  // Primary brand
  brand: '#00ACA9',
  brandDark: '#0C6B5F',
  brandLight: '#e6f7f6',

  // Neutral
  white: '#ffffff',
  black: '#000000',
  gray50: '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray300: '#cbd5e1',
  gray400: '#94a3b8',
  gray500: '#64748b',
  gray700: '#334155',
  gray900: '#0f172a',

  // Text
  textPrimary: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',

  // Chart colors — matches useChart.ts colorMap exactly
  chart: {
    teal: '#1da295',
    purple: '#9333ea',
    blue: '#3b82f6',
    red: '#ef4444',
    green: '#10b981',
    yellow: '#eab308',
    orange: '#f97316',
    pink: '#ec4899',
    indigo: '#6366f1',
    cyan: '#06b6d4',
    borderMuted: '#cbd5e1',
    border: '#e2e8f0',
  },
} as const;

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------
export const PDF_FONTS = {
  sans: 'Public Sans',
  sansSemiBold: 'Public Sans',
  sansBold: 'Public Sans',
} as const;

// Font weight constants to pair with the above family names.
// react-pdf resolves the correct face via fontWeight in StyleSheet.
export const PDF_FONT_WEIGHTS = {
  regular: 400,
  semiBold: 600,
  bold: 700,
} as const;

export const PDF_FONT_SIZES = {
  xs: 7,
  sm: 8,
  base: 14,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
} as const;

export const PDF_LINE_HEIGHTS = {
  tight: 1.2,
  normal: 1.4,
  relaxed: 1.6,
} as const;

// ---------------------------------------------------------------------------
// Spacing (pt — react-pdf default unit)
// ---------------------------------------------------------------------------
export const PDF_SPACING = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
} as const;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
export const PDF_PAGE = {
  // A4 in points
  width: 595.28,
  height: 841.89,

  // Content page margins (internal pages)
  marginTop: 30,
  marginBottom: 30,
  marginLeft: 50,
  marginRight: 50,

  cardBleed: 12,

  // Derived content width
  get contentWidth() {
    return this.width - this.marginLeft - this.marginRight;
  },
} as const;

// ---------------------------------------------------------------------------
// Shared text styles — use these across all report pages
// ---------------------------------------------------------------------------
export const PDF_TEXT_STYLES = {
  pageTitle: {
    fontFamily: 'Public Sans' as const,
    fontWeight: 300,
    fontSize: 18,
    lineHeight: 1.35,
    color: '#25252A',
  },
  pageSubtitle: {
    fontFamily: 'Public Sans' as const,
    fontWeight: 300,
    fontSize: 12,
    lineHeight: 1.35,
    color: '#5E5E69',
  },
  body: {
    fontFamily: 'Public Sans' as const,
    fontWeight: 300,
    fontSize: 10,
    lineHeight: 1.35,
    color: '#25252A',
  },
} as const;

export const PDF_BORDER_RADIUS = {
  sm: 4,
  md: 8,
  lg: 16,
} as const;

// ---------------------------------------------------------------------------
// Dev-only guard for fallback content
// ---------------------------------------------------------------------------

/**
 * Returns the value only in development builds.
 * In production, returns undefined so the caller's nullish chain produces
 * no output rather than showing hardcoded placeholder text.
 *
 * Usage:  apiValue ?? devOnly(FALLBACK_TEXT)
 */
export function devOnly<T>(value: T): T | undefined {
  return import.meta.env.DEV ? value : undefined;
}
